import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Lock, User, Sun, Moon, ArrowRight } from 'lucide-react';
import logger from '../utils/logger';

const Login = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { login, isAuthenticated } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Корисничкото име е задолжително';
    if (!formData.password) newErrors.password = 'Лозинката е задолжителна';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const result = await login(formData.username, formData.password);
      if (result.success) navigate(from, { replace: true });
    } catch (error) {
      logger.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-page)', padding: '1.5rem', position: 'relative', overflow: 'hidden',
    }}>
      {/* atmosphere */}
      <div style={{ position: 'absolute', inset: 0, background:
        'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(16,185,129,.12), transparent 70%)', pointerEvents: 'none' }} />
      <div className="login-grid" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />

      {/* theme toggle */}
      <button onClick={toggle} aria-label="Тема"
        style={{ position: 'absolute', top: 18, right: 18, width: 42, height: 42, borderRadius: 12,
          border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-secondary)',
          display: 'grid', placeItems: 'center', cursor: 'pointer', zIndex: 5 }}>
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div style={{ position: 'relative', width: '100%', maxWidth: 410, zIndex: 2 }}>
        {/* brand */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo.svg`}
            alt="Ovatik Fleet"
            width={60}
            height={60}
            style={{ width: 60, height: 60, borderRadius: 16, boxShadow: '0 12px 26px -8px rgba(5,150,105,.5)', marginBottom: '0.9rem' }}
          />
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text-primary)', margin: 0 }}>Ovatik <span style={{ color: 'var(--color-primary)' }}>Fleet</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', margin: '0.35rem 0 0', fontSize: '0.9rem' }}>
            Систем за следење на возен парк
          </p>
        </div>

        {/* card */}
        <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
          borderRadius: 20, boxShadow: 'var(--shadow-lg)', padding: '1.9rem' }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label"><User size={15} style={{ marginRight: '0.4rem', display: 'inline', verticalAlign: '-2px' }} />Корисничко име</label>
              <input type="text" name="username" value={formData.username} onChange={handleChange}
                className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                placeholder="Вашето корисничко име" disabled={loading} autoFocus />
              {errors.username && <div className="invalid-feedback">{errors.username}</div>}
            </div>

            <div className="form-group">
              <label className="form-label"><Lock size={15} style={{ marginRight: '0.4rem', display: 'inline', verticalAlign: '-2px' }} />Лозинка</label>
              <input type="password" name="password" value={formData.password} onChange={handleChange}
                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                placeholder="Вашата лозинка" disabled={loading} />
              {errors.password && <div className="invalid-feedback">{errors.password}</div>}
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ width: '100%', padding: '0.8rem', fontSize: '0.95rem', marginTop: '0.5rem',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', borderRadius: 12 }}>
              {loading ? (<><div className="spinner" style={{ marginRight: '0.5rem' }} />Најавување…</>) : (<>Најави се <ArrowRight size={16} /></>)}
            </button>

            <div style={{ textAlign: 'center', marginTop: '1.4rem' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Немате профил? <Link to="/register" style={{ color: 'var(--color-primary)', fontWeight: 700, textDecoration: 'none' }}>Регистрирајте се</Link>
              </p>
            </div>
          </form>
        </div>

        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '1.5rem' }}>
          © 2026 Ovatik Fleet · Сите права се задржани
        </p>
      </div>
    </div>
  );
};

export default Login;
